import React from 'react';
import type { WidgetJSON } from '../../transformers/zod-schemas';
import type { ValidationStatus } from './types';
import { InlineInput } from './InlineInput';
import { DragOptions, InlineDragOption } from './drag-widgets';
import { RadioOptions } from './RadioOptions';
import { DiamondKeypad } from './DiamondKeypad';
import { NumericKeypad } from './NumericKeypad';
import { FractionInput } from './FractionInput';
import { MatcherWidget } from './matcher';
import { PairMatcherWidget } from './pair-matcher';
import { VisualFraction } from './visual-fraction';
import {
  isInlineInputWidget,
  isDragOptionsWidget,
  isInlineDragOptionWidget,
  isRadioOptionsWidget,
  isNumericKeypadWidget,
  isFractionWidget,
  isMatcherWidget,
  isPairMatcherWidget,
  isVisualFractionWidget,
} from './type-guards';
import type { UserAnswerValue } from '../types';
import type { WidgetValidation } from '../../validators';

export type { ValidationStatus } from './types';

export interface WidgetRendererProps {
  widget: WidgetJSON;
  widgetId: string;
  value: UserAnswerValue | undefined;
  values?: Record<string, string>;
  isDropped?: boolean;
  validationStatus?: ValidationStatus;
  numValidationStatus?: ValidationStatus;
  denValidationStatus?: ValidationStatus;
  validationDetail?: WidgetValidation;
  droppedInputs?: Set<string>;
  /** All widgets in the exercise — needed so MatcherWidget can resolve refs in its items. */
  widgets?: Record<string, WidgetJSON>;
  onChange: (widgetId: string, value: UserAnswerValue, fromDrop?: boolean, sourceOptionId?: string) => void;
  onInputFocus?: (widgetId: string) => void;
  onKeypadChange?: (value: string) => void;
}

/**
 * WidgetRenderer - Router component that delegates to specific widget components
 * This is a thin layer that just switches between different widget types
 * Each widget handles its own behavior (DnD, buttons, input, etc.)
 */
export const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  widgetId,
  value,
  values = {},
  isDropped = false,
  validationStatus = null,
  numValidationStatus = null,
  denValidationStatus = null,
  validationDetail,
  droppedInputs = new Set<string>(),
  widgets,
  onChange,
  onInputFocus,
  onKeypadChange,
}) => {
  // Scalar widgets always receive a string value.
  const strValue = typeof value === 'string' ? value : '';

  if (isInlineInputWidget(widget)) {
    return (
      <InlineInput
        widgetId={widgetId}
        value={strValue}
        isDropped={isDropped}
        validationStatus={validationStatus}
        inputType={widget.inputType}
        length={widget.inputType === 'string' ? widget.length : undefined}
        digits={widget.inputType === 'numeric' ? widget.digits : undefined}
        placeholder={widget.placeholder}
        onChange={onChange}
        onFocus={onInputFocus}
      />
    );
  }

  if (isDragOptionsWidget(widget)) {
    return (
      <DragOptions
        options={widget.options}
        onOptionClick={() => {}}
      />
    );
  }

  if (isInlineDragOptionWidget(widget)) {
    // Drag only — same as DragOptions panel: click-to-select not wired because
    // the same chip widgetId can appear multiple times, making the target ambiguous
    return (
      <InlineDragOption
        widgetId={widgetId}
        widget={widget}
      />
    );
  }

  if (isRadioOptionsWidget(widget)) {
    return (
      <RadioOptions
        name={widgetId}
        options={widget.options}
        value={strValue}
        validationStatus={validationStatus}
        onChange={onChange}
      />
    );
  }

  if (isNumericKeypadWidget(widget)) {
    // Keypad updates the focused input directly, not itself
    const keypadOnChange = onKeypadChange || (() => {});
    if (widget.style === 'classic') {
      return (
        <NumericKeypad
          value={strValue}
          includeOperators={widget.includeOperators}
          includeDecimal={widget.includeDecimal}
          onChange={keypadOnChange}
        />
      );
    }
    return (
      <DiamondKeypad
        value={strValue}
        onChange={keypadOnChange}
      />
    );
  }

  if (isFractionWidget(widget)) {
    return (
      <FractionInput
        widgetId={widgetId}
        numerator={widget.numerator}
        denominator={widget.denominator}
        values={values}
        droppedInputs={droppedInputs}
        validationStatus={validationStatus}
        numValidationStatus={numValidationStatus}
        denValidationStatus={denValidationStatus}
        onChange={onChange}
        onFocus={onInputFocus}
        placeholder={widget.placeholder}
        />
    );
  }
  if (isMatcherWidget(widget)) {
    return (
      <MatcherWidget
        widgetId={widgetId}
        widget={widget}
        onChange={onChange}
        validationStatus={validationStatus}
        defaultValue={Array.isArray(value) ? value as string[][] : undefined}
        widgets={widgets}
      />
    );
  }

  if (isPairMatcherWidget(widget)) {
    return (
      <PairMatcherWidget
        widgetId={widgetId}
        widget={widget}
        onChange={onChange}
        validationStatus={validationStatus}
        validationDetail={validationDetail}
        defaultValue={Array.isArray(value) ? value as string[][] : undefined}
        widgets={widgets}
      />
    );
  }

  if (isVisualFractionWidget(widget)) {
    return (
      <VisualFraction
        widgetId={widgetId}
        widget={widget}
        value={Array.isArray(value) && value.every(v => typeof v === 'number') ? value : undefined}
        validationStatus={validationStatus}
        onChange={(id, nextValue) => onChange(id, nextValue)}
      />
    );
  }

  // Exhaustive check - should never reach here
  return null;
};